import type { ReportScatterPlotData } from "@repo/app-types/entities/report";
import { ChartSkeleton } from "@repo/ui/Skeletons/skeletons";
import { useMemo, type FC, type ReactNode } from "react";
import {
  CartesianGrid,
  Cell,
  Label,
  ReferenceLine,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
  type TooltipContentProps,
} from "recharts";
import { BaseCard } from "./baseCard.js";
import { BaseTooltipContent } from "./baseTooltipContent.js";
import { renderValueAsString } from "./values.js";

export interface ScatterPlotCardProps {
  title: ReactNode;
  data?: ReportScatterPlotData;
}

export const ScatterPlotCard: FC<ScatterPlotCardProps> = (props) => {
  const { data } = props;

  const diagonalBounds = useMemo(() => {
    if (!data?.highlightDiagonal || data.points.length === 0) return undefined;
    const values = data.points.flatMap(({ x, y }) => [x, y]);
    return { min: Math.min(...values), max: Math.max(...values) };
  }, [data?.highlightDiagonal, data?.points]);

  const regressionSegment = useMemo(() => {
    if (!data?.showRegressionLine || data.points.length < 2) return undefined;
    return fitLinearRegressionSegment(data.points);
  }, [data?.showRegressionLine, data?.points]);

  return (
    <BaseCard title={props.title}>
      {data ? (
        <>
          {data.points.length === 0 ? (
            <div className="flex flex-auto items-center justify-center py-12 text-center text-sm text-gray-400">
              No data
            </div>
          ) : (
            <div className="-m-[5px] mt-2">
              <ScatterChart
                style={{
                  width: "100%",
                  aspectRatio: "2 / 1",
                }}
                margin={{
                  top: 5,
                  right: 5,
                  bottom: data.xLabel ? 20 : 5,
                  left: data.yLabel ? 30 : 5,
                }}
                responsive
              >
                <CartesianGrid
                  stroke="var(--color-gray-200)"
                  strokeWidth={1}
                  vertical={false}
                />
                <XAxis
                  type="number"
                  dataKey="x"
                  stroke="none"
                  tick={{
                    fontSize: "0.75rem",
                    fill: "var(--color-gray-500)",
                  }}
                  tickFormatter={(value) =>
                    renderValueAsString({
                      value,
                      valueType: data.xValueType,
                    })
                  }
                  domain={["auto", "auto"]}
                >
                  {data.xLabel ? (
                    <Label
                      value={data.xLabel}
                      position="insideBottom"
                      offset={-5}
                      style={{
                        fontSize: "0.75rem",
                        fill: "var(--color-gray-500)",
                      }}
                    />
                  ) : null}
                </XAxis>
                <YAxis
                  type="number"
                  dataKey="y"
                  width="auto"
                  stroke="none"
                  tick={{
                    fontSize: "0.75rem",
                    fill: "var(--color-gray-500)",
                  }}
                  tickFormatter={(value) =>
                    renderValueAsString({
                      value,
                      valueType: data.yValueType,
                    })
                  }
                  domain={["auto", "auto"]}
                >
                  {data.yLabel ? (
                    <Label
                      value={data.yLabel}
                      angle={-90}
                      position="insideLeft"
                      offset={-15}
                      style={{
                        fontSize: "0.75rem",
                        fill: "var(--color-gray-500)",
                        textAnchor: "middle",
                      }}
                    />
                  ) : null}
                </YAxis>
                <ZAxis range={[60, 60]} />
                {diagonalBounds ? (
                  <ReferenceLine
                    ifOverflow="extendDomain"
                    segment={[
                      { x: diagonalBounds.min, y: diagonalBounds.min },
                      { x: diagonalBounds.max, y: diagonalBounds.max },
                    ]}
                    stroke="var(--color-gray-400)"
                    strokeDasharray="3 3"
                  />
                ) : null}
                {regressionSegment ? (
                  <ReferenceLine
                    ifOverflow="extendDomain"
                    segment={regressionSegment}
                    stroke="var(--color-gray-400)"
                    strokeDasharray="3 3"
                  />
                ) : null}
                <Scatter
                  data={data.points.map(({ label, x, y }) => ({
                    label,
                    x,
                    y,
                  }))}
                  fillOpacity={0.5}
                >
                  {data.points.map(({ x, y }, index) => (
                    <Cell
                      key={index}
                      fill={
                        data.highlightDiagonal && y < x
                          ? "var(--color-red-500)"
                          : "var(--color-emerald-500)"
                      }
                    />
                  ))}
                </Scatter>
                <Tooltip
                  cursor={{ strokeDasharray: "3 3" }}
                  content={(tooltipProps) => (
                    <ScatterPlotTooltipContent
                      {...tooltipProps}
                      xValueType={data.xValueType}
                      yValueType={data.yValueType}
                      xLabel={data.xLabel}
                      yLabel={data.yLabel}
                    />
                  )}
                />
              </ScatterChart>
            </div>
          )}
        </>
      ) : (
        <div style={{ aspectRatio: 1.618, maxHeight: "70vh" }}>
          <ChartSkeleton />
        </div>
      )}
    </BaseCard>
  );
};

function fitLinearRegressionSegment(
  points: ReportScatterPlotData["points"],
): [{ x: number; y: number }, { x: number; y: number }] | undefined {
  const n = points.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  let minX = Infinity;
  let maxX = -Infinity;
  for (const { x, y } of points) {
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
  }
  const denominator = n * sumXX - sumX * sumX;
  if (denominator === 0) return undefined;
  const slope = (n * sumXY - sumX * sumY) / denominator;
  const intercept = (sumY - slope * sumX) / n;
  return [
    { x: minX, y: slope * minX + intercept },
    { x: maxX, y: slope * maxX + intercept },
  ];
}

const ScatterPlotTooltipContent: FC<
  TooltipContentProps & {
    xValueType: ReportScatterPlotData["xValueType"];
    yValueType: ReportScatterPlotData["yValueType"];
    xLabel?: string;
    yLabel?: string;
  }
> = (props) => {
  if (!props.active || !props.payload?.length) {
    return null;
  }

  const point = props.payload[0]?.payload as
    | { label?: string; x: number; y: number }
    | undefined;
  if (!point) return null;

  return (
    <BaseTooltipContent
      label={
        point.label ??
        renderValueAsString({
          value: point.x,
          valueType: props.xValueType,
          unitDisplay: "long",
        })
      }
      entries={[
        ...(point.label
          ? [
              {
                label: props.xLabel,
                value: renderValueAsString({
                  value: point.x,
                  valueType: props.xValueType,
                  unitDisplay: "long",
                }),
              },
            ]
          : []),
        {
          color: props.payload[0]?.color,
          label: props.yLabel,
          value: renderValueAsString({
            value: point.y,
            valueType: props.yValueType,
            unitDisplay: "long",
          }),
        },
      ]}
    />
  );
};
